import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import CountdownTimer from '../components/CountdownTimer';
import LocationMap from '../components/LocationMap';
import '../styles/AshKennedy.css';

const TOTAL_SPOTS = 35;
const RESERVED_SPOTS = parseInt(process.env.REACT_APP_RESERVE_SPOTS || '0', 10);
const RSVP_COUNT_URL = process.env.REACT_APP_RSVP_COUNT_URL || 'https://getrsvpcount-fhil5zanna-uc.a.run.app';
const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';

export default function PartyRSVP() {
  const [formData, setFormData] = useState({
    parentName: '',
    email: '',
    phone: '',
    childNames: '',
    numberOfGuests: 1,
    dietaryRestrictions: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isStandbySubmitted, setIsStandbySubmitted] = useState(false);
  const [spotsRemaining, setSpotsRemaining] = useState(null);
  const [loadingSpots, setLoadingSpots] = useState(true);

  const fetchRSVPCount = async () => {
    try {
      const response = await fetch(`${RSVP_COUNT_URL}?partyId=kennedy-asher-2025`);
      const data = await response.json();
      const totalKids = data.totalKids || 0;
      setSpotsRemaining(TOTAL_SPOTS - RESERVED_SPOTS - totalKids);
    } catch (error) {
      console.error('Error fetching RSVP count:', error);
      // Fallback: show max spots if we can't fetch
      setSpotsRemaining(TOTAL_SPOTS - RESERVED_SPOTS);
    } finally {
      setLoadingSpots(false);
    }
  };

  useEffect(() => {
    fetchRSVPCount();
  }, []);

  // DEV_MODE forces standby view for testing
  const isFull = DEV_MODE || (spotsRemaining !== null && spotsRemaining <= 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isFull) {
      return;
    }
    
    if (spotsRemaining !== null && formData.numberOfGuests > spotsRemaining) {
      alert(`Only ${spotsRemaining} spot${spotsRemaining === 1 ? '' : 's'} remaining. Please reduce the number of children.`);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await addDoc(collection(db, 'rsvps'), {
        ...formData,
        partyId: 'kennedy-asher-2025',
        timestamp: new Date()
      });
      setIsSubmitted(true);
      // Optimistically update the count
      setSpotsRemaining(prev => prev - parseInt(formData.numberOfGuests));
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      alert('There was an error submitting your RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStandbySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await addDoc(collection(db, 'rsvps'), {
        ...formData,
        partyId: 'kennedy-asher-2025',
        isStandby: true,
        timestamp: new Date()
      });
      setIsStandbySubmitted(true);
    } catch (error) {
      console.error('Error submitting standby:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rsvp-page">
      <motion.div 
        className="rsvp-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1>Kennedy & Asher's Birthday!</h1>
        
        {DEV_MODE && (
          <div className="dev-banner">DEV MODE: Standby form forced</div>
        )}
        
        {!loadingSpots && (
          <div className="spots-indicator">
            <span className="spots-count">{Math.max(0, spotsRemaining)}</span>
            <span className="spots-label">spots remaining</span>
          </div>
        )}
        
        <div className="party-details">
          <CountdownTimer targetDate="2026-01-31T12:00:00" />
          
          <p className="date">Saturday, January 31st, 2026</p>
          <p className="time">12:00 PM - 2:00 PM</p>
          
          <div className="location">
            <span className="venue">Bounce It Up</span>
            <span className="address">30276 Plymouth Rd.</span>
            <span className="city">Livonia, MI</span>
          </div>

          <div className="party-info">
            <div className="info-item">
              <span className="info-title">Before You Go</span>
              <span className="info-text">
                Please review the <a href="https://bounceituplivonia.com/birthday-party-livonia-mi-about/guidlines/" target="_blank" rel="noopener noreferrer">safety rules</a>
              </span>
            </div>
            <div className="info-item">
              <span className="info-title">Food & Drinks</span>
              <span className="info-text">Pizza, drinks, & birthday cake provided</span>
            </div>
          </div>

          <div className="no-gifts-section">
            <h3 className="no-gifts-title">No Physical Gifts Please!</h3>
            <p className="no-gifts-text">
              If you would like to give a gift, contributions to their college savings accounts would be greatly appreciated!
            </p>
            <div className="qr-codes-container">
              <a href="https://www.ugift529.com/gifttpl/ugift/create/viewCollectGifterDetail.cs?ugiftcode=G82-N9B" target="_blank" rel="noopener noreferrer" className="qr-code-item">
                <img src="/images/Kennedy_ugift.png" alt="Kennedy's 529 QR Code" className="qr-code-image" />
                <span className="qr-code-label">Kennedy</span>
              </a>
              <a href="https://www.ugift529.com/gifttpl/ugift/create/viewCollectGifterDetail.cs?ugiftcode=W2C-G2J" target="_blank" rel="noopener noreferrer" className="qr-code-item">
                <img src="/images/Asher_ugift.png" alt="Asher's 529 QR Code" className="qr-code-image" />
                <span className="qr-code-label">Asher</span>
              </a>
            </div>
          </div>

          <LocationMap />
        </div>
        
        {isFull && !isStandbySubmitted ? (
          <motion.form 
            className="rsvp-form standby-form"
            onSubmit={handleStandbySubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <h3>Join the Standby List</h3>
            <p className="standby-notice">All spots have been filled, but you can join our standby list! We'll contact you if a spot opens up.</p>
            
            <div className="form-group">
              <label>Parent/Guardian Name</label>
              <input
                type="text"
                name="parentName"
                value={formData.parentName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Name(s) of child(ren)</label>
              <input
                type="text"
                name="childNames"
                value={formData.childNames}
                onChange={handleChange}
                placeholder="e.g., Emma, Jack"
                required
              />
            </div>

            <div className="form-group">
              <label>Number of Children</label>
              <select
                name="numberOfGuests"
                value={formData.numberOfGuests}
                onChange={handleChange}
              >
                {[1, 2, 3, 4, 5].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            <motion.button
              type="submit"
              className="submit-button standby-button"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? 'Sending...' : 'Join Standby List'}
            </motion.button>
          </motion.form>
        ) : isFull && isStandbySubmitted ? (
          <motion.div 
            className="success-message standby-success"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3>You're on the standby list!</h3>
            <p>We'll contact you if a spot becomes available.</p>
            <p>Thank you for your interest in Kennedy & Asher's party!</p>
          </motion.div>
        ) : !isSubmitted ? (
          <motion.form 
            className="rsvp-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <h3>RSVP</h3>
            
            <div className="form-group">
              <label>Parent/Guardian Name</label>
              <input
                type="text"
                name="parentName"
                value={formData.parentName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Name(s) of child(ren) attending</label>
              <input
                type="text"
                name="childNames"
                value={formData.childNames}
                onChange={handleChange}
                placeholder="e.g., Emma, Jack"
                required
              />
            </div>

            <div className="form-group">
              <label>Number of Children Attending</label>
              <select
                name="numberOfGuests"
                value={formData.numberOfGuests}
                onChange={handleChange}
              >
                {[1, 2, 3, 4, 5].filter(num => spotsRemaining === null || num <= spotsRemaining).map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Dietary Restrictions or Allergies</label>
              <input
                type="text"
                name="dietaryRestrictions"
                value={formData.dietaryRestrictions}
                onChange={handleChange}
                placeholder="e.g., nut allergy, gluten-free"
              />
            </div>

            <div className="form-group">
              <label>Notes or Questions</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                placeholder="Anything else we should know?"
              />
            </div>

            <motion.button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? 'Sending...' : 'Submit RSVP'}
            </motion.button>
          </motion.form>
        ) : (
          <motion.div 
            className="success-message"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3>Thank you for your RSVP!</h3>
            <p>We can't wait to celebrate with you!</p>
            <p>See you at Bounce It Up on January 31st!</p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
